import React, { useState, useEffect } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    Checkbox,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    TablePagination,
    TextField,
    Chip,
    IconButton,
    Tooltip,
    CircularProgress,
    LinearProgress,
    withStyles
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Skeleton } from "@material-ui/lab";
import { CSVLink } from "react-csv";
import HttpService from '../services/httpService';
import { setSnackInfo } from "../redux/actions/appAction";
import { connect } from "react-redux";
import moment from "moment";
import { FaEye } from "react-icons/fa";
import { LiaCogSolid } from "react-icons/lia";
// Material icons throughout instead of the Ant Design outlined set. The two
// families have different stroke weights and corner radii, so mixing them with
// the MUI controls already on this page made it look assembled from spare
// parts - the same swap was made across the call-recording pages.
import CardGiftcardOutlinedIcon from "@material-ui/icons/CardGiftcardOutlined";
import FilterListIcon from "@material-ui/icons/FilterList";
import DateRangeOutlinedIcon from "@material-ui/icons/DateRangeOutlined";
import GetAppIcon from "@material-ui/icons/GetApp";
import ClearAllIcon from "@material-ui/icons/ClearAll";
import AccountBalanceOutlinedIcon from "@material-ui/icons/AccountBalanceOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ScheduleOutlinedIcon from "@material-ui/icons/ScheduleOutlined";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import InboxOutlinedIcon from "@material-ui/icons/InboxOutlined";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import CreditCardOutlinedIcon from "@material-ui/icons/CreditCardOutlined";
import ConfirmationNumberOutlinedIcon from "@material-ui/icons/ConfirmationNumberOutlined";
import MonetizationOnOutlinedIcon from "@material-ui/icons/MonetizationOnOutlined";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import CloseIcon from "@material-ui/icons/Close";
import RefreshIcon from "@material-ui/icons/Refresh";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";

// Brand orange - sampled from the actual Decorpot logo, same palette used
// across the rest of the app's recently-redesigned pages.
const ORANGE = "#F47E44";
const ORANGE_DARK = "#c1531f";
const ORANGE_LIGHT = "#fff1ea";
const HEADER_GRADIENT = `linear-gradient(135deg, ${ORANGE_DARK} 0%, ${ORANGE} 60%, #ff9c66 100%)`;
const GREEN = "#1e8449";
const GREEN_LIGHT = "#eafaf1";
// Matches the "Silka bold" heading treatment used on the lead details
// (manageProjectLog) page, so headings here read consistently with the rest
// of the app. Body text already inherits "Silka regular" globally via App.css.
const HEADING_FONT = "'Silka bold', 'Silka semiBold', sans-serif !important";

const toNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

const formatAmount = (value) => `₹${toNumber(value).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const formatPoints = (value) => toNumber(value).toLocaleString("en-IN");

// Two-letter monogram for the referrer/referee cells.
const initialsOf = (name = "") =>
    String(name).trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

const PAYMENT_STATUS_LABELS = {
    all: "All",
    paid: "Paid",
    partially_paid: "Partially Paid",
    unpaid: "Unpaid",
};

// Brand-tinted indeterminate bar, used wherever a fetch is in flight.
const BrandLinearProgress = withStyles({
    root: { height: 3, backgroundColor: ORANGE_LIGHT },
    bar: { backgroundColor: ORANGE },
})(LinearProgress);

const useStyles = makeStyles(() => ({
    page: { padding: 20, background: "#f6f7fb", minHeight: "100vh" },
    headerCard: {
        marginBottom: 20,
        borderRadius: 16,
        overflow: "hidden",
        boxShadow: "0 10px 28px rgba(196,83,31,0.2)",
        background: HEADER_GRADIENT,
        padding: "22px 26px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 16,
    },
    headerLeft: { display: "flex", alignItems: "center", gap: 16 },
    headerIconWrap: {
        width: 52,
        height: 52,
        borderRadius: "50%",
        background: "rgba(255,255,255,0.22)",
        border: "2px solid rgba(255,255,255,0.35)",
        boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    statChip: {
        background: "rgba(255,255,255,0.2)",
        color: "#fff",
        borderRadius: 20,
        padding: "7px 16px",
        fontWeight: 700,
        fontSize: 13,
        display: "flex",
        alignItems: "center",
        gap: 6,
        whiteSpace: "nowrap",
    },
    downloadBtn: {
        borderRadius: 10,
        textTransform: "none",
        fontWeight: 600,
        color: "#fff",
        background: `linear-gradient(135deg, #146c43, ${GREEN})`,
        boxShadow: "none",
        padding: "8px 18px",
        "&:hover": { background: "linear-gradient(135deg, #0f5132, #146c43)", boxShadow: "0 6px 16px rgba(30,132,73,0.35)" },
        "&.Mui-disabled": { background: "rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.6)" },
    },
    csvLink: {
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        borderRadius: 10,
        textTransform: "none",
        fontWeight: 600,
        fontSize: 14,
        color: "#fff",
        textDecoration: "none",
        background: `linear-gradient(135deg, #146c43, ${GREEN})`,
        padding: "9px 18px",
        transition: "background 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
            color: "#fff",
            textDecoration: "none",
            background: "linear-gradient(135deg, #0f5132, #146c43)",
            boxShadow: "0 6px 16px rgba(30,132,73,0.35)",
        },
    },
    filterCard: {
        borderRadius: 16,
        boxShadow: "0 6px 20px rgba(17,24,39,0.06)",
        padding: "18px 22px",
        marginBottom: 20,
        background: "#fff",
    },
    sectionLabel: {
        display: "flex",
        alignItems: "center",
        gap: 6,
        color: "#6b7280",
        fontSize: 12.5,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.4,
        marginBottom: 10,
    },
    // Section icons sit in a small gradient tile rather than floating as a bare
    // grey glyph - gives the headings the same weight as the page header badge.
    iconBadge: {
        width: 28,
        height: 28,
        borderRadius: 9,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        color: "#fff",
        fontSize: 13,
        background: `linear-gradient(135deg, ${ORANGE_DARK}, ${ORANGE})`,
        boxShadow: "0 3px 9px rgba(244,126,68,0.38)",
    },
    iconBadgeMuted: {
        background: ORANGE_LIGHT,
        color: ORANGE_DARK,
        boxShadow: "none",
        border: `1px solid ${ORANGE}33`,
    },
    filterRow: { display: "flex", flexWrap: "wrap", gap: 14 },
    select: {
        minWidth: 190,
        flex: "1 1 190px",
        // The notched outline inherits the root's radius in MUI v4, so rounding
        // the root is enough to round the whole control.
        '& .MuiOutlinedInput-root': { borderRadius: 12, backgroundColor: "#fff" },
        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: `${ORANGE}88` },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ORANGE },
        '& .MuiInputLabel-root.Mui-focused': { color: ORANGE_DARK },
    },
    dateField: {
        // Fixed width, no flex-grow - it shouldn't stretch to fill whatever
        // space is left in the row, just a bit roomier than the bare minimum.
        width: 225,
        flex: "0 0 auto",
        '& .MuiOutlinedInput-root': { borderRadius: 12, backgroundColor: "#fff" },
        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: `${ORANGE}88` },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ORANGE },
        '& .MuiInputLabel-root.Mui-focused': { color: ORANGE_DARK },
    },
    submitBtn: {
        borderRadius: 10,
        textTransform: "none",
        fontWeight: 600,
        color: "#fff",
        background: `linear-gradient(135deg, ${ORANGE_DARK}, ${ORANGE})`,
        boxShadow: "none",
        padding: "8px 20px",
        "&:hover": { background: `linear-gradient(135deg, #a8441a, ${ORANGE_DARK})`, boxShadow: "0 6px 16px rgba(244,126,68,0.35)" },
        "&:disabled": { background: "rgba(0,0,0,0.12)", color: "rgba(0,0,0,0.38)" },
    },
    clearBtn: {
        borderRadius: 10,
        textTransform: "none",
        fontWeight: 600,
        color: "#6b7280",
        border: "1.5px solid #d8dee8",
        background: "#fff",
        padding: "8px 20px",
        "&:hover": { background: "#f5f6f8", borderColor: "#c3cad6" },
    },
    tableCard: { borderRadius: 16, overflow: "hidden", boxShadow: "0 6px 20px rgba(17,24,39,0.06)", background: "#fff" },
    tableWrap: { maxHeight: 650, overflow: "auto" },
    tableHeadCell: {
        // Solid, not a gradient: across a wide table the gradient's light end
        // left the right-hand headers washed out (white on #ff9c66 is only
        // ~2:1), and a flat band keeps every label at 4.64:1.
        // `!important` is still needed: MUI's stickyHeader rule paints its
        // own background on th.
        background: `${ORANGE_DARK} !important`,
        color: "#fff",
        fontWeight: 700,
        fontSize: 11.5,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        textAlign: "center",
        whiteSpace: "nowrap",
        borderBottom: "none",
    },
    tableRow: {
        "&:nth-of-type(odd)": { backgroundColor: "#FBFBFA" },
        "&:hover": { backgroundColor: ORANGE_LIGHT, boxShadow: `inset 3px 0 0 ${ORANGE}` },
        transition: "background-color .12s ease, box-shadow .12s ease",
    },
    tableCell: {
        padding: "10px",
        textAlign: "center",
        fontSize: 13.5,
        color: "#2b3648",
        borderBottom: "1px solid #f1f2f5",
    },
    rankCell: { color: "#9aa1ac", fontWeight: 800, fontVariantNumeric: "tabular-nums" },
    // Identity, not decoration: the same referrer appears on many rows, and an
    // initial makes a repeated name findable while scanning.
    personCell: { display: "flex", alignItems: "center", justifyContent: "center", gap: 8 },
    personAvatar: {
        width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: ORANGE_LIGHT, color: ORANGE_DARK,
        fontSize: 11, fontWeight: 800,
    },
    // Money and points are compared down the column, so they get tabular
    // figures - proportional digits made the amounts impossible to line up.
    amountCell: {
        fontWeight: 800,
        color: ORANGE_DARK,
        fontVariantNumeric: "tabular-nums",
        whiteSpace: "nowrap",
    },
    dateStack: { lineHeight: 1.35 },
    dateMain: { fontSize: 12.5, fontWeight: 700, color: "#2b3648", whiteSpace: "nowrap" },
    dateSub: { fontSize: 11, color: "#9aa1ac", whiteSpace: "nowrap" },
    tokenPill: {
        display: "inline-flex", alignItems: "center",
        padding: "2px 10px", borderRadius: 999,
        fontSize: 12, fontWeight: 700, fontVariantNumeric: "tabular-nums",
        background: "#f1f4f8", color: "#5c6779",
    },
    // A pill rather than a bare Button: it reads as the thing you click, and
    // matches the lead-number treatment on the BotWot dashboard.
    leadLink: {
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "3px 10px",
        borderRadius: 7,
        minWidth: 0,
        background: ORANGE_LIGHT,
        color: ORANGE_DARK,
        fontWeight: 700,
        fontSize: 12.5,
        textTransform: "none",
        lineHeight: 1.6,
        "&:hover": { background: "#ffe3d2" },
    },
    "@keyframes spin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
    "@keyframes pulseDot": {
        "0%, 100%": { opacity: 1, transform: "scale(1)" },
        "50%": { opacity: 0.45, transform: "scale(0.82)" },
    },
    // Solid white with a dark ring: the ring defines the EDGE, since white on
    // the bright #ff9c66 end of the gradient is only about 2:1. The word
    // "Refresh" is what makes the control findable at all.
    refreshButton: {
        display: "inline-flex", alignItems: "center", gap: 7,
        padding: "8px 16px", borderRadius: 999, border: "none",
        background: "#fff", color: ORANGE_DARK,
        fontSize: 12.5, fontWeight: 800, letterSpacing: 0.2, fontFamily: "inherit",
        cursor: "pointer",
        boxShadow: "0 0 0 1px rgba(0,0,0,0.14), 0 3px 10px rgba(0,0,0,0.24)",
        transition: "background .18s ease, transform .18s ease, box-shadow .18s ease",
        "&:hover:not(:disabled)": {
            background: ORANGE_LIGHT, transform: "translateY(-1px)",
            boxShadow: "0 0 0 1px rgba(0,0,0,0.18), 0 6px 16px rgba(0,0,0,0.3)",
        },
        "&:active:not(:disabled)": { transform: "translateY(0)" },
        "&:disabled": { cursor: "wait", opacity: 0.85 },
    },
    refreshSpinning: { animation: "$spin 0.9s linear infinite" },
    lastUpdatedText: {
        display: "inline-flex", alignItems: "center", gap: 6,
        fontSize: 11, fontWeight: 600, color: "#fff",
        background: "rgba(0,0,0,0.42)", padding: "3px 10px", borderRadius: 999,
        whiteSpace: "nowrap",
    },
    liveDot: {
        width: 6, height: 6, borderRadius: "50%", flexShrink: 0,
        background: "#FFD2B5", animation: "$pulseDot 2s ease-in-out infinite",
    },
    loadingBanner: {
        display: "flex", alignItems: "center", gap: 10,
        marginBottom: 14, padding: "10px 14px", borderRadius: 12,
        background: ORANGE_LIGHT, border: "1px solid #f6d3bd",
    },
    // Was underlined blue text, which read as a stray hyperlink in a table of
    // plain cells and gave no hint that it opens elsewhere. Now the whole
    // avatar + name is one hoverable chip carrying a launch glyph, so the
    // affordance is the shape rather than a text decoration.
    refPerson: {
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "3px 10px 3px 3px",
        borderRadius: 999,
        border: "1px solid transparent",
        cursor: "pointer",
        maxWidth: "100%",
        transition: "background-color .15s ease, border-color .15s ease",
        "&:hover": {
            background: ORANGE_LIGHT,
            borderColor: `${ORANGE}55`,
        },
        // Hover only strengthens the glyph - it is already legible at rest.
        "&:hover $refLaunch": { opacity: 1 },
    },
    refName: {
        fontWeight: 600,
        color: "#2b3648",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    // Brand orange at 0.85, not grey at 0.4. The old resting state measured
    // 1.41:1 against the row - below even the 3:1 bar for graphical objects,
    // so the glyph was effectively invisible until hovered, which defeats the
    // point of having an affordance at all. This sits at 3.64:1 on white and
    // 3.54:1 on the zebra row, and hover takes it to full strength.
    refLaunch: {
        fontSize: 13,
        flexShrink: 0,
        opacity: 0.85,
        color: ORANGE_DARK,
        transition: "opacity .15s ease",
    },
    // A flat grey icon floating with no background reads as disabled/inactive
    // - giving it a soft tinted circle (same treatment as the eye/bank icon
    // button) and a solid brand-colored glyph makes it clearly interactive.
    settingsBtn: {
        width: 32,
        height: 32,
        borderRadius: "50%",
        background: ORANGE_LIGHT,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        transition: "transform 0.2s ease, background-color 0.2s ease",
        "&:hover": { background: "#ffe3d2", transform: "rotate(70deg) scale(1.1)" },
    },
    emptyState: { padding: "60px 20px", textAlign: "center", color: "#9aa1ac" },
    emptyIconWrap: {
        width: 80,
        height: 80,
        borderRadius: "50%",
        background: ORANGE_LIGHT,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 auto 14px",
    },
    dialogHeader: {
        background: HEADER_GRADIENT,
        color: "#fff",
        padding: "18px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
    },
    dialogHeaderTitle: {
        display: "flex",
        alignItems: "center",
        gap: 10,
    },
    dialogCloseBtn: {
        color: "rgba(255,255,255,0.9)",
        "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
    },
    infoRow: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 0",
        borderBottom: "1px solid #f0f1f5",
    },
    infoIconWrap: {
        width: 30,
        height: 30,
        borderRadius: "50%",
        background: ORANGE_LIGHT,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    statusCard: {
        marginTop: 16,
        border: "1px solid #f0f1f5",
        borderRadius: 12,
        padding: "14px 16px",
        background: "#fafbfc",
        display: "flex",
        alignItems: "center",
        gap: 10,
    },
    filterHeaderRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 10,
        marginBottom: 12,
    },
    filterCount: {
        background: ORANGE_LIGHT,
        color: ORANGE_DARK,
        fontWeight: 700,
        fontSize: 11.5,
        height: 22,
    },
    loadingBadge: {
        display: "flex",
        alignItems: "center",
        gap: 7,
        padding: "4px 12px",
        borderRadius: 999,
        background: ORANGE_LIGHT,
        color: ORANGE_DARK,
        fontSize: 12,
        fontWeight: 700,
    },
    // Dims and locks the controls while a fetch is in flight, so it is obvious
    // the inputs are momentarily inert rather than broken.
    filterBody: { transition: "opacity 0.2s ease" },
    activeChipsRow: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
        marginTop: 16,
        paddingTop: 14,
        borderTop: "1px dashed #e7eaf0",
    },
    activeChip: {
        background: "#fff",
        border: `1px solid ${ORANGE}55`,
        color: ORANGE_DARK,
        fontWeight: 600,
        fontSize: 12,
    },
    modeChip: {
        background: "#eef2ff",
        color: "#4155a6",
        fontWeight: 700,
        fontSize: 12,
    },
    // Two-step dialog indicator, so it is obvious which payout is on screen
    // and that a second one follows.
    stepper: { display: "flex", alignItems: "center", gap: 8, marginTop: 10 },
    stepDot: {
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 12px",
        borderRadius: 999,
        fontSize: 11.5,
        fontWeight: 700,
        whiteSpace: "nowrap",
    },
    stepActive: { background: "rgba(255,255,255,0.26)", color: "#fff" },
    stepInactive: { background: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.65)" },
    stageChip: {
        background: "#eef2ff",
        color: "#4155a6",
        fontWeight: 600,
        fontSize: 12,
    },
    typeChip: {
        background: ORANGE_LIGHT,
        color: ORANGE_DARK,
        fontWeight: 700,
        fontSize: 12,
    },
}));

// Colored chip for a computed payment/gift status string - purely visual,
// same "Paid / Partially Paid / Unpaid" values as before.
const PaymentStatusChip = ({ status }) => {
    const meta = {
        "Paid": { bg: GREEN_LIGHT, color: GREEN, icon: <CheckCircleOutlineIcon style={{ fontSize: 14 }} /> },
        "Partially Paid": { bg: "#fff8e1", color: "#b8860b", icon: <ScheduleOutlinedIcon style={{ fontSize: 14 }} /> },
        "Unpaid": { bg: "#fdecea", color: "#c0392b", icon: <HighlightOffIcon style={{ fontSize: 14 }} /> },
    }[status] || { bg: "#f3f4f6", color: "#6b7280", icon: null };

    return (
        <Chip
            size="small"
            icon={meta.icon}
            label={status}
            style={{ background: meta.bg, color: meta.color, fontWeight: 700, fontSize: 12 }}
        />
    );
};

const InfoRow = ({ icon, label, value, onClick, classes }) => (
    <Box className={classes.infoRow}>
        <Box className={classes.infoIconWrap}>{icon}</Box>
        <Box style={{ flex: 1 }}>
            <Typography variant="caption" style={{ color: "#9aa1ac", display: "block" }}>{label}</Typography>
            <Typography
                style={{
                    fontWeight: 600,
                    color: onClick ? ORANGE_DARK : "#1f2937",
                    cursor: onClick ? "pointer" : "default",
                    textDecoration: onClick ? "underline" : "none",
                }}
                onClick={onClick}
            >
                {value ?? "N/A"}
            </Typography>
        </Box>
    </Box>
);

const Referral = (props) => {
    const classes = useStyles();
    const [referrals, setReferrals] = useState([]);
    const [selectedLead, setSelectedLead] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogStep, setDialogStep] = useState(1);
    const [referralMode, setReferralMode] = useState("Manual")
    const [filterType, setFilterType] = useState("all");
    const [openBankDialog, setOpenBankDialog] = useState(false);
    const [selectedBankDetails, setSelectedBankDetails] = useState(null);
    const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
    const [stageFilter, setStageFilter] = useState("all");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [totalCount, setTotalCount] = useState(0);
    const [dateRange, setDateRange] = useState({
        startDate: "",
        endDate: ""
    });
    // Local, button/table-level loaders instead of the app-wide Backdrop, so
    // refreshing/toggling gives immediate, visible feedback right where the
    // click happened (same pattern used across the app's redesigned pages).
    const [dataLoading, setDataLoading] = useState(false);
    // When the rows on screen were last pulled, so the header can say how stale
    // they are. Only set on a successful fetch - a failed refresh must not make
    // old rows look fresh.
    const [lastUpdated, setLastUpdated] = useState(null);
    // "Updated 4 minutes ago" is only true at the instant it renders, so a slow
    // tick re-renders it. 30s is plenty - moment rounds to the minute anyway.
    const [nowTick, setNowTick] = useState(Date.now());
    useEffect(() => {
        const id = setInterval(() => setNowTick(Date.now()), 30000);
        return () => clearInterval(id);
    }, []);
    const [togglingPaymentType, setTogglingPaymentType] = useState(null);

    const showBankDialog = (bankDetails) => {
        setSelectedBankDetails(bankDetails);
        setOpenBankDialog(true);
    };

    const handleCloseBankDialog = () => {
        setOpenBankDialog(false);
        setSelectedBankDetails(null);
    };

    useEffect(() => {
        fetchReferralData(filterType, paymentStatusFilter, stageFilter, referralMode, page + 1, rowsPerPage, dateRange.startDate, dateRange.endDate);
        // eslint-disable-next-line
    }, [filterType, paymentStatusFilter, stageFilter, referralMode, page, rowsPerPage]);


    const fetchReferralData = async (type, paymentStatus, stageFilter, referralMode, page, limit, startDate = null, endDate = null) => {
        setDataLoading(true);
        try {
            const response = await HttpService.getReferralData(type, paymentStatus, stageFilter, referralMode, page, limit, startDate, endDate)
            setReferrals(response.data.data);
            setTotalCount(response.data.pagination.total);
            // Inside the try, after the assignments: a fetch that threw leaves
            // the previous timestamp alone rather than vouching for stale rows.
            setLastUpdated(new Date());
        } catch (error) {
            console.error("Error fetching referrals:", error);
        }
        setDataLoading(false);
    };
    // Handle date range changes
    const handleDateChange = (e) => {
        const { name, value } = e.target;
        setDateRange(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle date filter submission
    const handleDateFilterSubmit = () => {
        setPage(0);
        fetchReferralData(
            filterType,
            paymentStatusFilter,
            stageFilter,
            referralMode,
            1,
            rowsPerPage,
            dateRange.startDate,
            dateRange.endDate
        );
    };

    // Clear all filters
    const handleClearFilters = () => {
        setFilterType("all");
        setPaymentStatusFilter("all");
        setStageFilter("all")
        setReferralMode("Manual")
        setDateRange({
            startDate: "",
            endDate: ""
        });
        setPage(0);
        fetchReferralData("all", "all", "all", "Manual", 1, rowsPerPage);
    };
    // Pagination function
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRowClick = (lead) => {
        setSelectedLead(lead);
        setDialogStep(1);
        setOpenDialog(true);
    };

    const handleNext = () => setDialogStep(2);
    const handlePrevious = () => setDialogStep(1);

    const handleCloseDialog = () => setOpenDialog(false);
    const togglePayment = async (type) => {
        if (!selectedLead) return;

        const paymentType = referralMode === "Manual"
            ? (type === "officeMeetReferralPayment"
                ? "officeMeetReferralPayment"
                : "kickOffReferralPayment")
            : (type === "siteOfficeVisitGift"
                ? "siteOfficeVisitGift"
                : "designSignOffGift");

        const newStatus = !selectedLead[paymentType]?.status;

        setTogglingPaymentType(paymentType);

        try {
            const response = await HttpService.paymentUpdate(
                selectedLead.leadId?._id,
                {
                    type: paymentType,
                    status: newStatus,
                    referralMode: referralMode,
                }
            );

            // Update selected lead state
            setSelectedLead((prevLead) => ({
                ...prevLead,
                [paymentType]: {
                    ...prevLead[paymentType],
                    status: newStatus,
                },
            }));

            // Update referrals list
            setReferrals((prevReferrals) =>
                prevReferrals.map((ref) =>
                    ref.leadId?._id === selectedLead.leadId?._id
                        ? {
                            ...ref,
                            [paymentType]: {
                                ...ref[paymentType],
                                status: newStatus,
                            },
                        }
                        : ref
                )
            );

            // Success message
            const successMessage =
                response?.data?.msg || "Payment status updated successfully";

            props.setSnackInfo(successMessage, "success");

            // Refresh data
            fetchReferralData(
                filterType,
                paymentStatusFilter,
                stageFilter,
                referralMode,
                page + 1,
                rowsPerPage,
                dateRange.startDate,
                dateRange.endDate
            );
        } catch (error) {
            console.error("Error updating payment status:", error);

            // Extract actual error from backend
            const errorMessage =
                error?.response?.data?.error ||   // backend error
                error?.response?.data?.msg ||     // fallback
                error?.message ||                 // axios/general
                "Error updating payment status";

            props.setSnackInfo(errorMessage, "error");
        }
        setTogglingPaymentType(null);
    };
    const handleNavigateToProjectLogs = () => {
        if (selectedLead?.leadId?._id) {
            window.open(`/project-logs/${selectedLead.leadId._id}`, '_blank');
        }
    };
    const handleNavigateToProjectLogsFromReferredBy = () => {
        if (selectedLead?.referrerLeadInfo?._id) {
            window.open(`/project-logs/${selectedLead?.referrerLeadInfo?._id}`, '_blank');
        }
    };

    // Same status computation as before, just centralized so both the table
    // chip and the dialog checkbox label read from one place.
    const getPaymentStatus = (item) => {
        if (referralMode === "Manual") {
            return item.officeMeetReferralPayment?.status && item.kickOffReferralPayment?.status
                ? "Paid"
                : item.officeMeetReferralPayment?.status || item.kickOffReferralPayment?.status
                    ? "Partially Paid"
                    : "Unpaid";
        }
        return item.siteOfficeVisitGift?.status && item.designSignOffGift?.status
            ? "Paid"
            : item.siteOfficeVisitGift?.status || item.designSignOffGift?.status
                ? "Partially Paid"
                : "Unpaid";
    };

    const isManual = referralMode === "Manual";

    // Total payout on a row, guarded - either leg can be missing, and a bare
    // `a + b` was rendering NaN in the table when one side was undefined.
    const rowTotal = (item) =>
        isManual
            ? toNumber(item?.officeMeetReferralPayment?.amount) + toNumber(item?.kickOffReferralPayment?.amount)
            : toNumber(item?.siteOfficeVisitGift?.points) + toNumber(item?.designSignOffGift?.points);

    // Bank Details only renders in Manual mode, so the loading/empty rows have
    // to span a different number of columns depending on the mode.
    const columnCount = isManual ? 12 : 11;

    // Dates are only applied on Submit, so clearing them has to refetch too -
    // the other filters refetch on their own via the effect above.
    const clearDateRange = () => {
        setDateRange({ startDate: "", endDate: "" });
        setPage(0);
        fetchReferralData(filterType, paymentStatusFilter, stageFilter, referralMode, 1, rowsPerPage);
    };

    const activeFilters = [
        filterType !== "all" && { key: "type", label: `Type: ${filterType}`, clear: () => setFilterType("all") },
        paymentStatusFilter !== "all" && {
            key: "payment",
            label: `Payment: ${PAYMENT_STATUS_LABELS[paymentStatusFilter] || paymentStatusFilter}`,
            clear: () => setPaymentStatusFilter("all"),
        },
        stageFilter !== "all" && { key: "stage", label: `Stage: ${stageFilter}`, clear: () => setStageFilter("all") },
        dateRange.startDate && dateRange.endDate && {
            key: "dates",
            label: `${moment(dateRange.startDate).format("DD MMM YY")} → ${moment(dateRange.endDate).format("DD MMM YY")}`,
            clear: clearDateRange,
        },
    ].filter(Boolean);

    const isTogglingCurrent = (type) => {
        const paymentType = referralMode === "Manual"
            ? (type === "officeMeetReferralPayment" ? "officeMeetReferralPayment" : "kickOffReferralPayment")
            : (type === "siteOfficeVisitGift" ? "siteOfficeVisitGift" : "designSignOffGift");
        return togglingPaymentType === paymentType;
    };

    return (
        <div className={classes.page}>
            {/* HEADER */}
            <Box className={classes.headerCard}>
                <Box className={classes.headerLeft}>
                    <Box className={classes.headerIconWrap}>
                        <CardGiftcardOutlinedIcon style={{ color: "#fff", fontSize: 26 }} />
                    </Box>
                    <Box>
                        <Typography style={{ color: "#fff", fontFamily: HEADING_FONT, fontSize: 21, margin: 0 }}>
                            Referral Program
                        </Typography>
                        <Typography style={{ color: "rgba(255,255,255,0.88)", fontSize: 13.5, marginTop: 4 }}>
                            Track and manage customer &amp; employee referral payouts
                        </Typography>
                    </Box>
                </Box>

                <Box style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                    <Box className={classes.statChip}>
                        <PersonOutlineIcon style={{ fontSize: 16 }} />
                        {totalCount} Referral{totalCount === 1 ? "" : "s"}
                    </Box>

                    <Tooltip
                        title={
                            lastUpdated
                                ? `Last pulled at ${moment(lastUpdated).format("DD MMM YYYY, hh:mm:ss A")} — click to fetch the latest referrals`
                                : "Fetch the latest referrals"
                        }
                    >
                        {/* A labelled button rather than an IconButton: the word
                            "Refresh" is what makes the control findable, and a
                            tooltip alone only helps people who already hovered.
                            The span is required because a disabled button fires
                            no mouse events, so the tooltip would die exactly
                            mid-refresh. */}
                        <span style={{ display: "inline-flex" }}>
                            <button
                                type="button"
                                className={classes.refreshButton}
                                onClick={() => fetchReferralData(
                                    filterType,
                                    paymentStatusFilter,
                                    stageFilter,
                                    referralMode,
                                    page + 1,
                                    rowsPerPage,
                                    dateRange.startDate,
                                    dateRange.endDate
                                )}
                                disabled={dataLoading}
                            >
                                <RefreshIcon
                                    className={dataLoading ? classes.refreshSpinning : undefined}
                                    style={{ fontSize: 17 }}
                                />
                                {dataLoading ? "Refreshing…" : "Refresh"}
                            </button>
                        </span>
                    </Tooltip>

                    {lastUpdated && (
                        <Box className={classes.lastUpdatedText}>
                            <span className={classes.liveDot} />
                            {/* Relative beats a clock time: it answers "is this
                                stale?" without the reader doing the arithmetic. */}
                            {dataLoading ? "Fetching latest…" : `Updated ${moment(lastUpdated).from(nowTick)}`}
                        </Box>
                    )}

                    {referrals.length ? (
                        <CSVLink
                            className={classes.csvLink}
                            data={referrals.map(item => {
                                return {
                                    lead_no: item?.leadId?.lead_no || "N/A",
                                    referralMode: referralMode,
                                    referralType: isManual ? item?.referalType : item?.referralType,
                                    referredBy: isManual ? item?.referedBy : item?.referredBy,
                                    referredTo: isManual ? item?.referedTo : item?.referredTo,
                                    referredOn: moment(item?.createdAt).format("DD-MM-YYYY"),

                                    // Manual Referral Fields
                                    ...(isManual && {
                                        officeMeetReferralAmount: item?.officeMeetReferralPayment?.amount || 0,
                                        officeMeetReferralPaymentDone: item?.officeMeetReferralPayment?.status ? "Paid" : "Unpaid",
                                        signOffReferralPayment: item?.kickOffReferralPayment?.amount || 0,
                                        signOffReferralPaymentDone: item?.kickOffReferralPayment?.status ? "Paid" : "Unpaid",
                                    }),

                                    // XoxodayGift Fields
                                    ...(!isManual && {
                                        siteOfficeVisitGiftPoints: item?.siteOfficeVisitGift?.points || 0,
                                        siteOfficeVisitGiftDone: item?.siteOfficeVisitGift?.status ? "Redeemed" : "Pending",
                                        designSignOffGiftPoints: item?.designSignOffGift?.points || 0,
                                        designSignOffGiftDone: item?.designSignOffGift?.status ? "Redeemed" : "Pending",
                                    })
                                };
                            })}
                            filename={`referrals_${referralMode}_${moment().format("YYYY-MM-DD")}.csv`}
                        >
                            <GetAppIcon />
                            Download CSV
                        </CSVLink>
                    ) : (
                        <Button variant="contained" className={classes.downloadBtn} startIcon={<GetAppIcon />} disabled>
                            Download CSV
                        </Button>
                    )}
                </Box>
            </Box>

            {/* The bar inside the filter card shows that something is happening;
                this says what, the way the call report does. */}
            {dataLoading && (
                <Box className={classes.loadingBanner}>
                    <CircularProgress size={17} thickness={5} style={{ color: ORANGE_DARK }} />
                    <Box style={{ minWidth: 0 }}>
                        <Typography style={{ fontSize: 13, fontWeight: 700, color: ORANGE_DARK }}>
                            Fetching referrals…
                        </Typography>
                        <Typography style={{ fontSize: 11.5, color: "#7a6a60" }}>
                            Pulling every referral for this selection — a wide date range can take a few seconds.
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* FILTERS */}
            <Box className={classes.filterCard} style={{ padding: 0, overflow: "hidden" }}>
                {dataLoading && <BrandLinearProgress />}
                <Box style={{ padding: "18px 22px" }}>
                    <Box className={classes.filterHeaderRow}>
                        <Typography className={classes.sectionLabel} style={{ marginBottom: 0, gap: 9 }}>
                            <span className={classes.iconBadge}>
                                <FilterListIcon style={{ fontSize: 15 }} />
                            </span>
                            Filters
                            {activeFilters.length > 0 && (
                                <Chip
                                    size="small"
                                    className={classes.filterCount}
                                    label={`${activeFilters.length} active`}
                                />
                            )}
                        </Typography>
                        {/* No loading badge here: the bar above and the banner
                            already say a fetch is running, and a third copy of
                            the same message was just noise. */}
                    </Box>

                    <Box
                        className={classes.filterBody}
                        style={{ opacity: dataLoading ? 0.55 : 1, pointerEvents: dataLoading ? "none" : "auto" }}
                    >
                        <Box className={classes.filterRow} style={{ marginBottom: 18 }}>
                            <FormControl variant="outlined" size="small" className={classes.select} disabled={dataLoading}>
                                <InputLabel>Mode of Referral</InputLabel>
                                <Select
                                    value={referralMode}
                                    onChange={(e) => setReferralMode(e.target.value)}
                                    label="Mode of Referral"
                                    disabled={dataLoading}
                                >
                                    <MenuItem value="Manual">Manual</MenuItem>
                                    <MenuItem value="XoxodayGift">Xoxoday Gift</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl variant="outlined" size="small" className={classes.select} disabled={dataLoading}>
                                <InputLabel>Referral Type</InputLabel>
                                <Select
                                    value={filterType}
                                    onChange={(e) => setFilterType(e.target.value)}
                                    label="Referral Type"
                                    disabled={dataLoading}
                                >
                                    <MenuItem value="all">All</MenuItem>
                                    <MenuItem value="Customer">Customer</MenuItem>
                                    <MenuItem value="Employee">Employee</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl variant="outlined" size="small" className={classes.select} disabled={dataLoading}>
                                <InputLabel>Payment Status</InputLabel>
                                <Select
                                    value={paymentStatusFilter}
                                    onChange={(e) => setPaymentStatusFilter(e.target.value)}
                                    label="Payment Status"
                                    disabled={dataLoading}
                                >
                                    <MenuItem value="all">All</MenuItem>
                                    <MenuItem value="paid">Paid</MenuItem>
                                    <MenuItem value="partially_paid">Partially Paid</MenuItem>
                                    <MenuItem value="unpaid">Unpaid</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl variant="outlined" size="small" className={classes.select} disabled={dataLoading}>
                                <InputLabel>Stage</InputLabel>
                                <Select
                                    value={stageFilter}
                                    onChange={(e) => setStageFilter(e.target.value)}
                                    label="Stage"
                                    disabled={dataLoading}
                                >
                                    <MenuItem value="all">All</MenuItem>
                                    <MenuItem value="Site/Office visit">Site/Office Visit</MenuItem>
                                    <MenuItem value="Design Sign-Off">Design Sign-Off</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        <Typography className={classes.sectionLabel} style={{ gap: 9 }}>
                            <span className={`${classes.iconBadge} ${classes.iconBadgeMuted}`}>
                                <DateRangeOutlinedIcon style={{ fontSize: 15 }} />
                            </span>
                            Date Range
                        </Typography>
                        <Box className={classes.filterRow} style={{ alignItems: "center" }}>
                            <TextField
                                id="date"
                                name="startDate"
                                label="From Date"
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                className={classes.dateField}
                                value={dateRange.startDate}
                                onChange={handleDateChange}
                                variant="outlined"
                                size="small"
                                disabled={dataLoading}
                            />

                            <TextField
                                id="endDate"
                                name="endDate"
                                label="End Date"
                                type="date"
                                value={dateRange.endDate}
                                onChange={handleDateChange}
                                InputLabelProps={{ shrink: true }}
                                className={classes.dateField}
                                variant="outlined"
                                size="small"
                                disabled={dataLoading}
                            />

                            <Tooltip title={!dateRange.startDate || !dateRange.endDate ? "Pick both a from and to date" : "Apply this date range"}>
                                <span>
                                    <Button
                                        variant="contained"
                                        className={classes.submitBtn}
                                        disabled={!dateRange.startDate || !dateRange.endDate || dataLoading}
                                        onClick={handleDateFilterSubmit}
                                        startIcon={dataLoading ? <CircularProgress size={15} style={{ color: "#fff" }} /> : null}
                                    >
                                        {dataLoading ? "Loading..." : "Submit"}
                                    </Button>
                                </span>
                            </Tooltip>

                            <Button
                                variant="outlined"
                                className={classes.clearBtn}
                                onClick={handleClearFilters}
                                startIcon={<ClearAllIcon />}
                                disabled={dataLoading}
                            >
                                Clear
                            </Button>
                        </Box>

                        <Box className={classes.activeChipsRow}>
                            <Typography style={{ fontSize: 12, color: "#8b93a6", fontWeight: 600 }}>
                                Showing:
                            </Typography>
                            <Chip
                                size="small"
                                className={classes.modeChip}
                                label={isManual ? "Manual" : "Xoxoday Gift"}
                            />
                            {activeFilters.map((f) => (
                                <Chip
                                    key={f.key}
                                    size="small"
                                    className={classes.activeChip}
                                    label={f.label}
                                    onDelete={f.clear}
                                    disabled={dataLoading}
                                />
                            ))}
                            {activeFilters.length === 0 && (
                                <Typography style={{ fontSize: 12, color: "#a4abb5" }}>
                                    All referrals — no extra filters applied
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* TABLE */}
            <Box className={classes.tableCard}>
                {dataLoading && <BrandLinearProgress />}
                <TableContainer className={classes.tableWrap}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.tableHeadCell}>Sl No</TableCell>
                                <TableCell className={classes.tableHeadCell}>Lead No</TableCell>
                                <TableCell className={classes.tableHeadCell}>Referred By</TableCell>
                                <TableCell className={classes.tableHeadCell}>Referred To</TableCell>
                                <TableCell className={classes.tableHeadCell}>Current Stage</TableCell>
                                <TableCell className={classes.tableHeadCell}>Token Percent</TableCell>
                                <TableCell className={classes.tableHeadCell}>Referred On</TableCell>
                                <TableCell className={classes.tableHeadCell}>Referral Type</TableCell>
                                <TableCell className={classes.tableHeadCell}>
                                    {referralMode === "Manual" ? "Total Amount" : "Total Points"}
                                </TableCell>
                                <TableCell className={classes.tableHeadCell}>Payment Status</TableCell>
                                {referralMode === "Manual" && (
                                    <TableCell className={classes.tableHeadCell}>Bank Details</TableCell>
                                )}
                                <TableCell className={classes.tableHeadCell}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {dataLoading ? (
                                Array.from({ length: 8 }).map((_, i) => (
                                    <TableRow key={`sk-${i}`}>
                                        {Array.from({ length: columnCount }).map((__, c) => (
                                            <TableCell key={c} className={classes.tableCell}>
                                                <Skeleton variant="text" width={c === 0 ? 24 : "80%"} style={{ margin: "0 auto" }} />
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : referrals.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={columnCount} style={{ border: "none" }}>
                                        <Box className={classes.emptyState}>
                                            <Box className={classes.emptyIconWrap}>
                                                <InboxOutlinedIcon style={{ fontSize: 36, color: ORANGE_DARK }} />
                                            </Box>
                                            <Typography style={{ fontWeight: 600, color: "#7d8794" }}>
                                                No referrals found
                                            </Typography>
                                            <Typography variant="body2" style={{ color: "#a4abb5", marginTop: 4 }}>
                                                Try adjusting your filters or date range.
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                referrals.map((item, index) => (
                                    <TableRow key={item._id} className={classes.tableRow}>
                                        <TableCell className={`${classes.tableCell} ${classes.rankCell}`}>{page * rowsPerPage + index + 1}</TableCell>

                                        <TableCell className={classes.tableCell}>
                                            <Tooltip title="Open this referral's payment details">
                                                <Button size="small" className={classes.leadLink} onClick={() => handleRowClick(item)}>
                                                    {item?.leadId?.lead_no || "N/A"}
                                                </Button>
                                            </Tooltip>
                                        </TableCell>

                                        <TableCell className={classes.tableCell}>
                                            {/* Only a link when there is actually a referrer lead to open. */}
                                            {(() => {
                                                const who = (isManual ? item?.referedBy : item?.referredBy) || "";
                                                if (!who) return <span style={{ color: "#9aa1ac" }}>N/A</span>;
                                                const referrerId = item?.referrerLeadInfo?._id;
                                                if (!referrerId) {
                                                    // No lead behind this name, so no affordance at all -
                                                    // a hover state that leads nowhere is worse than none.
                                                    return (
                                                        <div className={classes.personCell}>
                                                            <span className={classes.personAvatar}>{initialsOf(who)}</span>
                                                            <strong style={{ fontWeight: 600 }}>{who}</strong>
                                                        </div>
                                                    );
                                                }
                                                return (
                                                    <Tooltip title={`Open ${who}'s lead page in a new tab`}>
                                                        <span
                                                            className={classes.refPerson}
                                                            onClick={() =>
                                                                window.open(`/project-logs/${referrerId}`, "_blank", "noopener,noreferrer")
                                                            }
                                                        >
                                                            <span className={classes.personAvatar}>{initialsOf(who)}</span>
                                                            <span className={classes.refName}>{who}</span>
                                                            <OpenInNewIcon className={classes.refLaunch} />
                                                        </span>
                                                    </Tooltip>
                                                );
                                            })()}
                                        </TableCell>

                                        <TableCell className={classes.tableCell}>
                                            {(() => {
                                                const who = (isManual ? item?.referedTo : item?.referredTo) || "";
                                                if (!who) return <span style={{ color: "#9aa1ac" }}>N/A</span>;
                                                return (
                                                    <div className={classes.personCell}>
                                                        <span className={classes.personAvatar}>{initialsOf(who)}</span>
                                                        <strong style={{ fontWeight: 600 }}>{who}</strong>
                                                    </div>
                                                );
                                            })()}
                                        </TableCell>

                                        <TableCell className={classes.tableCell}>
                                            {item.stage ? (
                                                <Chip size="small" label={item.stage} className={classes.stageChip} />
                                            ) : (
                                                "N/A"
                                            )}
                                        </TableCell>
                                        <TableCell className={classes.tableCell}>
                                            {item?.leadId?.tokenPercent != null
                                                ? <span className={classes.tokenPill}>{item.leadId.tokenPercent}</span>
                                                : <span style={{ color: "#9aa1ac" }}>N/A</span>}
                                        </TableCell>
                                        <TableCell className={classes.tableCell}>
                                            {item.createdAt ? (
                                                <div className={classes.dateStack}>
                                                    <div className={classes.dateMain}>{moment(item.createdAt).format("DD MMM YYYY")}</div>
                                                    <div className={classes.dateSub}>{moment(item.createdAt).fromNow()}</div>
                                                </div>
                                            ) : <span style={{ color: "#9aa1ac" }}>N/A</span>}
                                        </TableCell>

                                        <TableCell className={classes.tableCell}>
                                            {(isManual ? item?.referalType : item?.referralType) ? (
                                                <Chip
                                                    size="small"
                                                    label={isManual ? item?.referalType : item?.referralType}
                                                    className={classes.typeChip}
                                                />
                                            ) : (
                                                "N/A"
                                            )}
                                        </TableCell>

                                        <TableCell className={`${classes.tableCell} ${classes.amountCell}`}>
                                            {isManual ? formatAmount(rowTotal(item)) : formatPoints(rowTotal(item))}
                                        </TableCell>

                                        <TableCell className={classes.tableCell}>
                                            <PaymentStatusChip status={getPaymentStatus(item)} />
                                        </TableCell>

                                        {referralMode === "Manual" && (
                                            <TableCell className={classes.tableCell}>
                                                {item.bankDetails ? (
                                                    <Tooltip title="View Bank Details">
                                                        <IconButton size="small" onClick={() => showBankDialog(item.bankDetails)}>
                                                            <FaEye size={16} style={{ color: ORANGE_DARK }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                ) : (
                                                    "NA"
                                                )}
                                            </TableCell>
                                        )}

                                        <TableCell className={classes.tableCell}>
                                            <Tooltip title="Manage Payment">
                                                <span className={classes.settingsBtn} onClick={() => handleRowClick(item)}>
                                                    <LiaCogSolid size={16} style={{ color: ORANGE_DARK }} />
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                {/* Outside the scroll area so it stays put instead of scrolling
                    away with the rows. */}
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[100, 200, 300, 500]}
                />
            </Box>

            {/* PAYMENT / GIFT STATUS DIALOG */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={{ style: { borderRadius: 16, overflow: "hidden" } }}>
                <Box className={classes.dialogHeader} style={{ alignItems: "flex-start" }}>
                    <Box>
                        <Box className={classes.dialogHeaderTitle}>
                            <CardGiftcardOutlinedIcon style={{ fontSize: 22 }} />
                            <Typography style={{ fontFamily: HEADING_FONT, fontSize: 17 }}>
                                {dialogStep === 1 ? "Site Visit Referral Payment" : "Design Signoff Referral Payment"}
                            </Typography>
                        </Box>
                        <Box className={classes.stepper}>
                            <span className={`${classes.stepDot} ${dialogStep === 1 ? classes.stepActive : classes.stepInactive}`}>
                                1 · Site Visit
                            </span>
                            <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 11 }}>›</span>
                            <span className={`${classes.stepDot} ${dialogStep === 2 ? classes.stepActive : classes.stepInactive}`}>
                                2 · Design Sign-Off
                            </span>
                        </Box>
                    </Box>
                    <IconButton size="small" className={classes.dialogCloseBtn} onClick={handleCloseDialog}>
                        <CloseIcon style={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
                <DialogContent style={{ paddingTop: 8 }}>
                    {selectedLead && (
                        <Box>
                            <InfoRow
                                classes={classes}
                                icon={<ConfirmationNumberOutlinedIcon style={{ color: ORANGE_DARK, fontSize: 16 }} />}
                                label="Lead No"
                                value={selectedLead.leadId?.lead_no}
                                onClick={handleNavigateToProjectLogs}
                            />
                            <InfoRow
                                classes={classes}
                                icon={<PersonOutlineIcon style={{ color: ORANGE_DARK, fontSize: 16 }} />}
                                label="Referred By"
                                value={referralMode === "Manual" ? selectedLead?.referedBy : selectedLead?.referredBy}
                                onClick={handleNavigateToProjectLogsFromReferredBy}
                            />
                            <InfoRow
                                classes={classes}
                                icon={<ArrowForwardIcon style={{ color: ORANGE_DARK, fontSize: 16 }} />}
                                label="Referred To"
                                value={referralMode === "Manual" ? selectedLead?.referedTo : selectedLead?.referredTo}
                            />
                            <InfoRow
                                classes={classes}
                                icon={<CardGiftcardOutlinedIcon style={{ color: ORANGE_DARK, fontSize: 16 }} />}
                                label="Referral Type"
                                value={referralMode === "Manual" ? selectedLead?.referalType : selectedLead?.referralType}
                            />
                            <InfoRow
                                classes={classes}
                                icon={<MonetizationOnOutlinedIcon style={{ color: ORANGE_DARK, fontSize: 16 }} />}
                                label={referralMode === "Manual" ? "Amount" : "Points"}
                                value={
                                    referralMode === "Manual"
                                        ? (
                                            dialogStep === 1
                                                ? selectedLead?.officeMeetReferralPayment?.amount
                                                : selectedLead?.kickOffReferralPayment?.amount
                                        )
                                        : (
                                            dialogStep === 1
                                                ? selectedLead?.siteOfficeVisitGift?.points
                                                : selectedLead?.designSignOffGift?.points
                                        )
                                }
                            />

                            {referralMode === "XoxodayGift" && (
                                <InfoRow
                                    classes={classes}
                                    icon={<MailOutlineIcon style={{ color: ORANGE_DARK, fontSize: 16 }} />}
                                    label="Referred By Email"
                                    value={selectedLead?.recipients}
                                />
                            )}

                            {/* Payment status toggle */}
                            <Box className={classes.statusCard}>
                                {isTogglingCurrent(dialogStep === 1
                                    ? (referralMode === "Manual" ? "officeMeetReferralPayment" : "siteOfficeVisitGift")
                                    : (referralMode === "Manual" ? "kickOffReferralPayment" : "designSignOffGift")
                                ) ? (
                                    <CircularProgress size={22} style={{ color: ORANGE }} />
                                ) : (
                                    <Checkbox
                                        style={{ color: ORANGE_DARK }}
                                        checked={
                                            !!(referralMode === "Manual"
                                                ? (dialogStep === 1
                                                    ? selectedLead?.officeMeetReferralPayment?.status
                                                    : selectedLead?.kickOffReferralPayment?.status)
                                                : (dialogStep === 1
                                                    ? selectedLead?.siteOfficeVisitGift?.status
                                                    : selectedLead?.designSignOffGift?.status))
                                        }
                                        disabled={
                                            referralMode !== "Manual" && // Only apply restriction in XoxodayGift Mode
                                            (
                                                dialogStep === 1
                                                    ? selectedLead?.siteOfficeVisitGift?.status
                                                    : selectedLead?.designSignOffGift?.status
                                            ) === true // Disable if already true
                                        }
                                        onChange={() => {
                                            if (referralMode === "Manual") {
                                                togglePayment(dialogStep === 1 ? "officeMeetReferralPayment" : "kickOffReferralPayment");
                                            } else {
                                                togglePayment(dialogStep === 1 ? "siteOfficeVisitGift" : "designSignOffGift");
                                            }
                                        }}
                                    />
                                )}
                                <Typography style={{ fontWeight: 600, color: "#374151" }}>
                                    {referralMode === "Manual"
                                        ? (dialogStep === 1 ? "Site Visit Referral Payment" : "Design Signoff Referral Payment")
                                        : (dialogStep === 1 ? "Site Visit Gift Status" : "Design Signoff Gift Status")}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions style={{ padding: "14px 24px 20px" }}>
                    {dialogStep === 2 && (
                        <Button onClick={handlePrevious} variant="contained" className={classes.submitBtn} startIcon={<ArrowBackIcon />}>
                            Previous
                        </Button>
                    )}
                    {selectedLead?.leadId?.leadStatus === "Won" && dialogStep === 1
                        ? (
                            <Button onClick={handleNext} variant="contained" className={classes.submitBtn} endIcon={<ArrowForwardIcon />}>
                                Next
                            </Button>
                        )
                        : (
                            <Button onClick={handleCloseDialog} variant="outlined" className={classes.clearBtn}>
                                Close
                            </Button>
                        )
                    }
                </DialogActions>
            </Dialog>

            {/* BANK DETAILS DIALOG */}
            <Dialog open={openBankDialog} onClose={handleCloseBankDialog} maxWidth="sm" fullWidth PaperProps={{ style: { borderRadius: 16, overflow: "hidden" } }}>
                <Box className={classes.dialogHeader}>
                    <Box className={classes.dialogHeaderTitle}>
                        <AccountBalanceOutlinedIcon style={{ fontSize: 22 }} />
                        <Typography style={{ fontFamily: HEADING_FONT, fontSize: 17 }}>Bank Details</Typography>
                    </Box>
                    <IconButton size="small" className={classes.dialogCloseBtn} onClick={handleCloseBankDialog}>
                        <CloseIcon style={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
                <DialogContent style={{ paddingTop: 8 }}>
                    {selectedBankDetails && (
                        <Box>
                            <InfoRow classes={classes} icon={<PersonOutlineIcon style={{ color: ORANGE_DARK, fontSize: 16 }} />} label="Account Holder" value={selectedBankDetails.accountHolder} />
                            <InfoRow classes={classes} icon={<AccountBalanceOutlinedIcon style={{ color: ORANGE_DARK, fontSize: 16 }} />} label="Bank Name" value={selectedBankDetails.bankName} />
                            <InfoRow classes={classes} icon={<AccountBalanceOutlinedIcon style={{ color: ORANGE_DARK, fontSize: 16 }} />} label="Branch Name" value={selectedBankDetails.branchName} />
                            <InfoRow classes={classes} icon={<CreditCardOutlinedIcon style={{ color: ORANGE_DARK, fontSize: 16 }} />} label="Account Number" value={selectedBankDetails.accountNumber} />
                            <InfoRow classes={classes} icon={<ConfirmationNumberOutlinedIcon style={{ color: ORANGE_DARK, fontSize: 16 }} />} label="IFSC Code" value={selectedBankDetails.ifscCode} />
                            <InfoRow classes={classes} icon={<MailOutlineIcon style={{ color: ORANGE_DARK, fontSize: 16 }} />} label="Email" value={selectedBankDetails.email} />
                            <InfoRow classes={classes} icon={<ConfirmationNumberOutlinedIcon style={{ color: ORANGE_DARK, fontSize: 16 }} />} label="Unique Lead No/ERP Project No" value={selectedBankDetails.uniqueLeadNumber} />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions style={{ padding: "14px 24px 20px" }}>
                    <Button onClick={handleCloseBankDialog} variant="contained" className={classes.submitBtn}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};


export default connect(null, { setSnackInfo })(Referral);
